import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  OutlinedInput,
  type SelectChangeEvent,
} from "@mui/material";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  slotProps: {
    paper: {
      style: {
        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
        width: 250,
      },
    },
  },
};

interface MultiSelectFilterProps<T extends string = string> {
  label: string;
  options: T[];
  selected: T[];
  onChange: (selected: T[]) => void;
  fullWidth?: boolean;
}

export const MultiSelectFilter = <T extends string = string>({
  label,
  options,
  selected,
  onChange,
  fullWidth = false,
}: MultiSelectFilterProps<T>) => {
  const handleChange = (event: SelectChangeEvent<T[]>) => {
    const {
      target: { value },
    } = event;
    onChange(
      // On autofill we get a stringified value.
      typeof value === "string" ? (value.split(",") as T[]) : value,
    );
  };

  return (
    // No margin when stretched: the caller lays these out in a spaced Grid,
    // where an extra 8px each side leaves this field indented and 16px wider
    // than every sibling. minWidth stays for the non-fullWidth case.
    <FormControl
      sx={fullWidth ? undefined : { minWidth: 200 }}
      size="small"
      fullWidth={fullWidth}
    >
      <InputLabel id={`${label}-label`}>{label}</InputLabel>
      <Select
        labelId={`${label}-label`}
        id={`${label}-select`}
        multiple
        value={selected}
        onChange={handleChange}
        input={<OutlinedInput label={label} />}
        renderValue={(selected) => selected.join(", ")}
        MenuProps={MenuProps}
      >
        {options.map((option) => (
          <MenuItem key={option} value={option}>
            <Checkbox checked={selected.indexOf(option) > -1} />
            <ListItemText primary={option} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};
